import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mantine/core';
import { IconPlayerPlay } from '@tabler/icons-react';
import SidebarNav from '@components/sidebar/sidebar-nav';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { useTextScale } from '@contexts/TextScaleContext';
import { sidebarSubAnchor } from '@/features/onboarding';
import { resolveFirstLoginCopy } from './i18n';
import type { FilmVariant } from './types';

/** The rail's own width at 100% text scale (content.tsx); scaled like the live rail. */
const RAIL_WIDTH = 240;
const FRAME_HEIGHT = 480;
/** How far the camera pushes in. */
const ZOOM = 1.25;
/** Where the Master row sits, as a fraction of the frame height, while its sub-list is shown. */
const MASTER_TARGET_Y = 0.18;
/** Where the Pengaturan row sits while the footer is shown. */
const PENGATURAN_TARGET_Y = 0.55;
/** Room above the Master row on the reduced-motion frame. */
const REDUCED_TOP_GAP = 12;

const MASTER_LABEL = 'Master';
const STATEMENT_LINK = '/master/statement';
const CONSIGNEE_LINK = '/master/consignee';

type Phase = 'rest' | 'zoom' | 'open' | 'statement' | 'consignee' | 'pengaturan' | 'done';

/** Timelines, ms from play. */
const TIMELINES: Record<FilmVariant, Array<[number, Phase]>> = {
  // Master, its two pages, then the footer.
  full: [
    [0, 'rest'],
    [700, 'zoom'],
    [1700, 'open'],
    [2900, 'statement'],
    [4100, 'consignee'],
    [5400, 'pengaturan'],
    [7400, 'done'],
  ],
  // No Master to show: straight to the footer.
  pengaturan: [
    [0, 'rest'],
    [900, 'pengaturan'],
    [3200, 'done'],
  ],
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const anchorSelector = (token: string) => `[data-tour="${token}"]`;
const masterGroupSelector = `[data-nav-group="${MASTER_LABEL}"]`;
/** The group's own toggle row: the wrapper grows with the sub-list once open. */
const masterRowSelector = `${masterGroupSelector} button`;
/** Footer rows carry `data-nav-footer` (not a tour anchor) so the film can find them. */
const pengaturanSelector = '[data-nav-footer="pengaturan"]';

interface Point {
  x: number;
  y: number;
}

interface Camera {
  /** Rail-relative y of the Master row centre. Scale origin. */
  originY: number;
  /** Rail-relative y of the Master row's top edge. */
  masterTop: number;
  /** Rail-relative y of the Pengaturan row centre, measured with Master open. */
  pengaturanY: number;
  /** Rail-relative y of the Pengaturan row's bottom edge, same measurement. */
  pengaturanBottom: number;
}

/**
 * Layout position of a row inside the rail, from the offset chain: unaffected
 * by the camera transform, and current for the rail's open/closed state at the
 * time of measuring (the footer moves down when Master opens).
 */
const layoutCentreY = (row: HTMLElement, rail: HTMLElement): number => {
  let y = row.offsetHeight / 2;
  let node: HTMLElement | null = row;
  while (node && node !== rail) {
    y += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return y;
};

/**
 * The step-3 film: the real rail (SidebarNav renders the same header, menus,
 * rows, footer, theme and motion the user's sidebar does), scripted. The
 * camera zooms toward Master, Master opens the way it opens on click,
 * Pernyataan and Data Consignee take their real active state, then the
 * camera pans down to the footer and Pengaturan takes its hover state. Every
 * position on screen is measured from the rendered rows; nothing is
 * hand-placed.
 */
export interface SidebarFilmProps {
  variant?: FilmVariant;
  /** Line under the frame; the step picks it to match the variant and situation. */
  caption: string;
}

export default function SidebarFilm({ variant = 'full', caption }: SidebarFilmProps) {
  const { colors: themeColors } = useTheme();
  const { language } = useLanguage();
  const { scale: textScale } = useTextScale();
  const t = (key: string, vars?: Record<string, string | number>) => resolveFirstLoginCopy(language, key, vars);

  const frameRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>('rest');
  const [cursor, setCursor] = useState<Point | null>(null);
  const [camera, setCamera] = useState<Camera | null>(null);
  // Bumped on every play. A reduced-motion replay goes rest -> pengaturan in
  // one batch, so `phase` alone does not change and the measuring effect
  // would not re-run.
  const [playCount, setPlayCount] = useState(0);
  const timersRef = useRef<number[]>([]);
  const reduced = useMemo(() => prefersReducedMotion(), []);

  const clearTimers = useCallback(() => {
    for (const id of timersRef.current) {
      window.clearTimeout(id);
    }
    timersRef.current = [];
  }, []);

  const schedule = useCallback((delay: number, fn: () => void) => {
    timersRef.current.push(window.setTimeout(fn, delay));
  }, []);

  /** Where the cursor should sit for a row inside the rail, relative to the frame. */
  const measure = useCallback((selector: string): Point | null => {
    const frame = frameRef.current;
    const rail = railRef.current;
    if (!frame || !rail) return null;
    const row = rail.querySelector<HTMLElement>(selector);
    if (!row) return null;
    const frameRect = frame.getBoundingClientRect();
    const rect = row.getBoundingClientRect();
    // Aim at the label, a third of the way in, rather than the exact centre.
    return { x: rect.left - frameRect.left + rect.width * 0.36, y: rect.top - frameRect.top + rect.height / 2 };
  }, []);

  /**
   * Master's position is fixed for the whole film; it is the scale origin.
   * Pengaturan is measured too, from the layout as it is now, so the camera
   * never carries a placeholder; aimFooter corrects it once Master is open.
   */
  const aimCamera = useCallback(() => {
    const rail = railRef.current;
    const master = rail?.querySelector<HTMLElement>(masterRowSelector);
    if (!rail || !master) return;
    const originY = layoutCentreY(master, rail);
    const masterTop = originY - master.offsetHeight / 2;
    const pengaturan = rail.querySelector<HTMLElement>(pengaturanSelector);
    const pengaturanY = pengaturan ? layoutCentreY(pengaturan, rail) : originY;
    const pengaturanBottom = pengaturan ? pengaturanY + pengaturan.offsetHeight / 2 : originY;
    setCamera({ originY, masterTop, pengaturanY, pengaturanBottom });
  }, []);

  /**
   * The footer sits below Master's open sub-list, so in the full film it is
   * measured once that is open. The Pengaturan-only film has no Master shot:
   * the footer row itself becomes the scale origin.
   */
  const aimFooter = useCallback(() => {
    const rail = railRef.current;
    const pengaturan = rail?.querySelector<HTMLElement>(pengaturanSelector);
    if (!rail || !pengaturan) return;
    const pengaturanY = layoutCentreY(pengaturan, rail);
    const pengaturanBottom = pengaturanY + pengaturan.offsetHeight / 2;
    setCamera((previous) =>
      previous
        ? { ...previous, pengaturanY, pengaturanBottom }
        : { originY: pengaturanY, masterTop: pengaturanY, pengaturanY, pengaturanBottom }
    );
  }, []);

  const play = useCallback(() => {
    clearTimers();
    setCursor(null);
    setCamera(null);
    setPhase('rest');
    setPlayCount((count) => count + 1);
    if (reduced) {
      // One static frame: Pengaturan highlighted, with Master open in the full film.
      if (variant === 'full') aimCamera();
      setPhase('pengaturan');
      return;
    }
    for (const [at, next] of TIMELINES[variant]) {
      schedule(at, () => {
        if (next === 'zoom') aimCamera();
        setPhase(next);
        // In the full film Master has been open since the 'open' phase, so the
        // footer is in its final place; in the short film it never moves.
        if (next === 'pengaturan') aimFooter();
      });
    }
  }, [aimCamera, aimFooter, clearTimers, reduced, schedule, variant]);

  // Cursor follows the row each phase is about, re-measured while the rail's
  // own expand/collapse and the camera move are still settling.
  useEffect(() => {
    if (reduced) return;
    const target =
      phase === 'zoom' || phase === 'open'
        ? masterRowSelector
        : phase === 'statement'
          ? anchorSelector(sidebarSubAnchor(MASTER_LABEL, 'Statement'))
          : phase === 'consignee'
            ? anchorSelector(sidebarSubAnchor(MASTER_LABEL, 'Data Consignee'))
            : phase === 'pengaturan'
              ? pengaturanSelector
              : null;
    if (!target) {
      setCursor(null);
      return;
    }
    const ids: number[] = [];
    // The camera transition is 900 ms; the last sample lands just after it.
    for (const delay of [60, 260, 520, 800, 950]) {
      ids.push(
        window.setTimeout(() => {
          const point = measure(target);
          if (point) setCursor(point);
        }, delay)
      );
    }
    return () => {
      for (const id of ids) window.clearTimeout(id);
    };
  }, [phase, measure, reduced]);

  // Reduced motion renders Master open in one commit; measure the footer after
  // it, on the first play and on every replay.
  useEffect(() => {
    if (reduced && phase === 'pengaturan') aimFooter();
  }, [reduced, phase, aimFooter, playCount]);

  useEffect(() => {
    const id = window.setTimeout(play, 350);
    return () => {
      window.clearTimeout(id);
      clearTimers();
    };
  }, [play, clearTimers]);

  const fullFilm = variant === 'full';
  const masterOpen =
    fullFilm && (phase === 'open' || phase === 'statement' || phase === 'consignee' || phase === 'pengaturan');
  const activeLink = !fullFilm
    ? ''
    : phase === 'statement'
      ? STATEMENT_LINK
      : phase === 'consignee' || phase === 'pengaturan'
        ? CONSIGNEE_LINK
        : '';
  const footerHighlight = phase === 'pengaturan' ? 'pengaturan' : undefined;

  const frameHeight = frameRef.current?.clientHeight ?? FRAME_HEIGHT;
  let cameraStyle: { transform: string; transformOrigin: string };
  if (reduced && camera) {
    // No camera move. Full film: slide so the Master row's top sits just
    // inside the frame at 1:1, and further up if that is what it takes for
    // Pengaturan to fit as well. Short film: only as far as Pengaturan needs.
    const forPengaturan = frameHeight - REDUCED_TOP_GAP - camera.pengaturanBottom;
    const slide = fullFilm ? Math.min(REDUCED_TOP_GAP - camera.masterTop, forPengaturan) : Math.min(0, forPengaturan);
    cameraStyle = { transform: `translateY(${slide}px) scale(1)`, transformOrigin: '0px 0px' };
  } else if (camera && phase === 'pengaturan') {
    // Same origin and scale as the Master shot, slid so Pengaturan lands where
    // the rows under it still show.
    const dy = frameHeight * PENGATURAN_TARGET_Y - camera.originY - (camera.pengaturanY - camera.originY) * ZOOM;
    cameraStyle = { transform: `translateY(${dy}px) scale(${ZOOM})`, transformOrigin: `0px ${camera.originY}px` };
  } else if (camera && (phase === 'zoom' || phase === 'open' || phase === 'statement' || phase === 'consignee')) {
    // Origin at the Master row: scaling leaves that row where it is, the
    // slide puts it where the opened sub-list has room below it.
    const dy = frameHeight * MASTER_TARGET_Y - camera.originY;
    cameraStyle = { transform: `translateY(${dy}px) scale(${ZOOM})`, transformOrigin: `0px ${camera.originY}px` };
  } else {
    // Rest: the top of the rail, as the app opens.
    cameraStyle = { transform: 'translateY(0px) scale(1)', transformOrigin: '0px 0px' };
  }

  return (
    <div>
      <div
        ref={frameRef}
        data-film-phase={phase}
        data-film-variant={variant}
        style={{
          position: 'relative',
          height: FRAME_HEIGHT,
          overflow: 'hidden',
          borderRadius: 12,
          border: `1px solid ${themeColors.border}`,
          backgroundColor: themeColors.background,
          backgroundImage: themeColors.backgroundGradient,
        }}
      >
        <div
          ref={railRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            // At least the frame's height: the real rail paints its surface to
            // the bottom of the viewport, so the column must not end early.
            minHeight: '100%',
            display: 'flex',
            alignItems: 'stretch',
            ...cameraStyle,
            transition: reduced ? 'none' : 'transform 900ms cubic-bezier(0.22, 1, 0.36, 1)',
            willChange: 'transform',
          }}
        >
          <div style={{ display: 'flex', alignSelf: 'stretch' }}>
            <SidebarNav
              inert
              width={Math.round(RAIL_WIDTH * textScale)}
              forceOpenGroup={masterOpen ? MASTER_LABEL : undefined}
              forceActiveLink={activeLink}
              layoutGroupId="first-login-film"
              showFooter
              footerHighlight={footerHighlight}
            />
          </div>
          {/* The page behind the rail, as blank as a freshly opened screen. It
              stretches to the rail's height and never sets one of its own. */}
          <div style={{ flex: 1, padding: 18, alignSelf: 'stretch' }} aria-hidden="true">
            <div style={{ height: 12, width: '40%', borderRadius: 4, backgroundColor: themeColors.border }} />
            {[0.95, 0.7, 0.55, 0.9, 0.7].map((w, index) => (
              <div
                key={`${index}-${w}`}
                style={{
                  height: 8,
                  width: `${w * 100}%`,
                  marginTop: 10,
                  borderRadius: 4,
                  backgroundColor: themeColors.border,
                  opacity: 0.6,
                }}
              />
            ))}
          </div>
        </div>

        {cursor && (
          <div
            data-film-cursor
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: cursor.x,
              top: cursor.y,
              width: 16,
              height: 20,
              pointerEvents: 'none',
              zIndex: 2,
              transition: 'left 520ms cubic-bezier(0.22, 1, 0.36, 1), top 520ms cubic-bezier(0.22, 1, 0.36, 1)',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))',
            }}
          >
            <svg viewBox="0 0 14 18" width="16" height="20" role="presentation">
              <path
                d="M1 1 L1 14 L4.5 10.8 L7 16.5 L9.5 15.4 L7 9.8 L12 9.6 Z"
                fill={themeColors.card}
                stroke={themeColors.text}
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
        <Button
          size="xs"
          variant="outline"
          leftSection={<IconPlayerPlay size={14} />}
          onClick={play}
          styles={{ root: { borderColor: themeColors.border, color: themeColors.text } }}
        >
          {t('film.replay')}
        </Button>
        <span style={{ fontSize: 13, color: themeColors.textSecondary }}>{caption}</span>
      </div>
    </div>
  );
}
